import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getCashiers, 
  createCashier, 
  updateCashier, 
  deleteCashier,
  getBranches,
  getPOSTerminals,
  forceLogoutTerminal
} from '../../services/api';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  X, 
  Check,
  Users,
  Key,
  Store,
  LogOut,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const Cajeros = () => {
  const { user } = useAuth();
  const [cashiers, setCashiers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCashier, setEditingCashier] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    pin: '',
    terminal_ids: []
  });

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.business_id) return;

    try {
      const [cashiersRes, branchesRes] = await Promise.all([
        getCashiers(user.business_id),
        getBranches(user.business_id)
      ]);
      
      setCashiers(cashiersRes.data);
      setBranches(branchesRes.data);
      
      // Load all terminals for all branches
      const allTerminals = [];
      for (const branch of branchesRes.data) {
        try {
          const terminalsRes = await getPOSTerminals(branch.id);
          allTerminals.push(...terminalsRes.data.map(t => ({
            ...t,
            branch_name: branch.name
          })));
        } catch (e) {
          console.error('Error loading terminals for branch:', branch.id);
        }
      }
      setTerminals(allTerminals);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (cashier = null) => {
    if (cashier) {
      setEditingCashier(cashier);
      setFormData({
        full_name: cashier.full_name,
        pin: '',
        terminal_ids: cashier.terminal_ids || []
      });
    } else {
      setEditingCashier(null);
      setFormData({
        full_name: '',
        pin: '',
        terminal_ids: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCashier(null);
    setFormData({ full_name: '', pin: '', terminal_ids: [] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    if (!editingCashier && (!formData.pin || formData.pin.length !== 6)) {
      toast.error('El PIN debe ser de 6 dígitos');
      return;
    }

    try {
      if (editingCashier) {
        const updateData = {
          full_name: formData.full_name,
          terminal_ids: formData.terminal_ids
        };
        if (formData.pin && formData.pin.length === 6) {
          updateData.pin = formData.pin;
        }
        await updateCashier(editingCashier.id, updateData);
        toast.success('Cajero actualizado');
      } else {
        await createCashier({
          ...formData,
          business_id: user.business_id
        });
        toast.success('Cajero creado exitosamente');
      }
      
      handleCloseModal();
      loadData();
    } catch (error) {
      console.error('Error saving cashier:', error);
      toast.error(getErrorMessage(error, 'Error al guardar cajero'));
    }
  };

  const handleDelete = async (cashier) => {
    if (!window.confirm(`¿Eliminar a ${cashier.full_name}?`)) return;
    
    try {
      await deleteCashier(cashier.id);
      toast.success('Cajero eliminado');
      loadData();
    } catch (error) {
      console.error('Error deleting cashier:', error);
      toast.error('Error al eliminar cajero');
    }
  };

  const handleForceLogout = async (terminal) => {
    if (!window.confirm(`¿Forzar cierre de sesión en ${terminal.name}?`)) return;
    
    try {
      await forceLogoutTerminal(terminal.id);
      toast.success('Sesión cerrada');
      loadData();
    } catch (error) {
      console.error('Error forcing logout:', error);
      toast.error('Error al cerrar sesión');
    }
  };

  const toggleTerminal = (terminalId) => {
    setFormData(prev => ({
      ...prev,
      terminal_ids: prev.terminal_ids.includes(terminalId)
        ? prev.terminal_ids.filter(id => id !== terminalId)
        : [...prev.terminal_ids, terminalId]
    }));
  };

  const getTerminalName = (terminalId) => {
    const terminal = terminals.find(t => t.id === terminalId);
    return terminal ? `${terminal.name} (${terminal.branch_name})` : terminalId;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Hash copiado al portapapeles');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Cajeros</h1>
            <p className="text-slate-600 mt-2">Gestiona los cajeros y sus permisos de terminales</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            data-testid="add-cashier-btn"
            className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nuevo Cajero
          </button>
        </div>

        {/* Cashiers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cashiers.length === 0 ? (
            <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No hay cajeros registrados</p>
              <p className="text-sm text-slate-400 mt-1">Crea un cajero para que pueda usar el POS</p>
            </div>
          ) : (
            cashiers.map(cashier => (
              <div 
                key={cashier.id} 
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{cashier.full_name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        cashier.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {cashier.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleOpenModal(cashier)}
                      className="p-2 text-slate-400 hover:text-teal-600 hover:bg-slate-50 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(cashier)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-slate-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Key className="w-4 h-4 text-slate-400" />
                    <span>PIN: ****</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Store className="w-4 h-4 text-slate-400" />
                    <span>{cashier.terminal_ids?.length || 0} terminales asignadas</span>
                  </div>
                </div>
                
                {cashier.terminal_ids?.length > 0 && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500 mb-2">Terminales:</p>
                    <div className="flex flex-wrap gap-1">
                      {cashier.terminal_ids.slice(0, 3).map(tid => (
                        <span key={tid} className="text-xs px-2 py-1 bg-slate-100 rounded text-slate-600">
                          {getTerminalName(tid).split(' (')[0]}
                        </span>
                      ))}
                      {cashier.terminal_ids.length > 3 && (
                        <span className="text-xs px-2 py-1 bg-slate-100 rounded text-slate-400">
                          +{cashier.terminal_ids.length - 3} más
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Terminals Status */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900">Estado de Terminales</h2>
            <p className="text-slate-600 text-sm mt-1">Monitorea las sesiones activas en cada terminal</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Terminal</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Sucursal</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Código de Instalación</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {terminals.map(terminal => (
                  <tr key={terminal.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{terminal.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{terminal.branch_name}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded">
                          {terminal.terminal_hash}
                        </code>
                        <button
                          onClick={() => copyToClipboard(terminal.terminal_hash)}
                          className="p-1 hover:bg-slate-200 rounded transition-colors"
                          title="Copiar"
                        >
                          <Copy className="w-3 h-3 text-slate-400" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {terminal.active_session_id ? (
                        <span className="inline-flex items-center gap-1 text-sm text-green-700 bg-green-100 px-2 py-1 rounded-full">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                          En uso
                        </span>
                      ) : (
                        <span className="text-sm text-slate-500">Libre</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {terminal.active_session_id && (
                        <button
                          onClick={() => handleForceLogout(terminal)}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          <LogOut className="w-3 h-3" />
                          Forzar cierre
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                {editingCashier ? 'Editar Cajero' : 'Nuevo Cajero'}
              </h2>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre Completo</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Ej: Juan Pérez García"
                  data-testid="cashier-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  PIN (6 dígitos) {editingCashier && <span className="text-slate-400 font-normal">- Dejar vacío para mantener</span>}
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={formData.pin}
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value)) {
                      setFormData({ ...formData, pin: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 tracking-[0.5em] text-center text-lg"
                  placeholder="••••••"
                  data-testid="cashier-pin-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Terminales Permitidas</label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-3">
                  {terminals.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">No hay terminales configuradas</p>
                  ) : (
                    terminals.map(terminal => (
                      <label key={terminal.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.terminal_ids.includes(terminal.id)}
                          onChange={() => toggleTerminal(terminal.id)}
                          className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        />
                        <div>
                          <p className="text-sm text-slate-900">{terminal.name}</p>
                          <p className="text-xs text-slate-500">{terminal.branch_name}</p>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors flex items-center justify-center gap-2"
                  data-testid="save-cashier-btn"
                >
                  <Check className="w-5 h-5" />
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Cajeros;
